import {
  useRef,
  useLayoutEffect,
  useCallback,
  useMemo,
  type MutableRefObject,
} from "react"
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  addDays,
  addMonths,
  isSameMonth,
} from "date-fns"
import DateRangePickerMonth from "./DateRangePickerMonth"

const MONTHS_BEFORE = 36
const MONTHS_AFTER = 12

function formatMonthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth()).padStart(2, "0")}`
}

function getMonthWeeks(month: Date): (Date | null)[][] {
  const monthEnd = endOfMonth(month)
  let weekStart = startOfWeek(startOfMonth(month), { weekStartsOn: 1 })

  const weeks: (Date | null)[][] = []
  while (weekStart <= monthEnd) {
    const week: (Date | null)[] = []
    for (let i = 0; i < 7; i++) {
      const day = addDays(weekStart, i)
      week.push(isSameMonth(day, month) ? day : null)
    }
    weeks.push(week)
    weekStart = addDays(weekStart, 7)
  }
  return weeks
}

type MonthData = {
  key: string
  date: Date
  weeks: (Date | null)[][]
}

function generateMonths(center: Date): MonthData[] {
  const months: MonthData[] = []
  for (let i = -MONTHS_BEFORE; i <= MONTHS_AFTER; i++) {
    const date = startOfMonth(addMonths(center, i))
    months.push({ key: formatMonthKey(date), date, weeks: getMonthWeeks(date) })
  }
  return months
}

type DateRangePickerGridProps = {
  focusMonth: Date
  setNavMonthRef: MutableRefObject<(month: Date) => void>
}

function DateRangePickerGrid(props: DateRangePickerGridProps) {
  const { focusMonth, setNavMonthRef } = props

  const scrollRef = useRef<HTMLDivElement>(null)
  const suppressScrollRef = useRef(false)
  const lastScrolledKeyRef = useRef("")

  const months = useMemo(() => generateMonths(focusMonth), [focusMonth])

  useLayoutEffect(() => {
    const key = formatMonthKey(focusMonth)
    lastScrolledKeyRef.current = key

    const container = scrollRef.current
    if (!container) return
    const el = container.querySelector(`[data-month="${key}"]`)
    if (!el) return

    suppressScrollRef.current = true
    el.scrollIntoView({ block: "start", behavior: "instant" as ScrollBehavior })
    requestAnimationFrame(() => {
      suppressScrollRef.current = false
    })
  }, [focusMonth])

  const handleScroll = useCallback(() => {
    if (suppressScrollRef.current) return
    const container = scrollRef.current
    if (!container) return

    const containerTop = container.getBoundingClientRect().top
    const sections = container.querySelectorAll<HTMLElement>("[data-month]")

    let closestKey = ""
    let closestDist = Number.POSITIVE_INFINITY

    for (const section of sections) {
      const dist = Math.abs(section.getBoundingClientRect().top - containerTop)
      if (dist < closestDist) {
        closestDist = dist
        closestKey = section.dataset.month ?? ""
      }
    }

    if (closestKey && closestKey !== lastScrolledKeyRef.current) {
      lastScrolledKeyRef.current = closestKey
      const [year, month] = closestKey.split("-").map(Number)
      setNavMonthRef.current(new Date(year, month, 1))
    }
  }, [setNavMonthRef])

  const rafRef = useRef<number | null>(null)
  function onScroll() {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(handleScroll)
  }

  return (
    <div ref={scrollRef} className="ui-date-picker-scroll" onScroll={onScroll}>
      {months.map((month) => (
        <DateRangePickerMonth
          key={month.key}
          monthKey={month.key}
          date={month.date}
          weeks={month.weeks}
        />
      ))}
    </div>
  )
}

export default DateRangePickerGrid
